import { toast } from "@/services/toast";
import { useEffect, useState } from "react";
import { AiFillCloseCircle } from "react-icons/ai";
import { IoMdEye, IoMdEyeOff } from "react-icons/io";
import { RiErrorWarningFill } from "react-icons/ri";
import { useNavigate, useSearchParams } from "react-router-dom";

import ErrorAlert from "@/components/ErrorAlert";
import WarningAlert from "@/components/WarningAlert";
import api from "@/services/api";
import { captureError } from "@/services/error";
import { hasLetter, hasNumber, hasSpecialChar } from "@/utils/string";

const Signup = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [user, setUser] = useState();
  const [error, setError] = useState();

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const res = await api.post("/user/verify-token", { token });
      if (!res.ok) {
        if (res.code === "NOT_FOUND") return setError("invalide");
        else if (res.code === "REQUEST_EXPIRED") return setError("expired");
        else throw res;
      }
      setUser(res.data);
    } catch (error) {
      captureError(error, { extra: { hasToken: !!token } });
    }
  };

  return (
    <div className="h-full w-full bg-white px-4 py-10 sm:px-32">
      <title>API Engagement - Inscription</title>
      {error === "invalide" ? (
        <ErrorAlert>
          <p className="text-xl font-bold">La clé n'est pas valide</p>
          <p className="text-color-grey-200 text-sm">La clé fournie n'est pas valide, veuillez nous contacter pour accedez à votre compte</p>
        </ErrorAlert>
      ) : error === "expired" ? (
        <WarningAlert>
          <p className="text-xl font-bold">La clé a expiré</p>
          <p className="text-color-grey-200 text-sm">La clé fournie est expirée, contactez nous pour avoir un nouveau mail d'inscription</p>
        </WarningAlert>
      ) : (
        <SignupForm user={user} token={token} />
      )}
    </div>
  );
};

const SignupForm = ({ user, token }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [values, setValues] = useState({
    firstname: "",
    lastname: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (user) {
      setValues({
        firstname: user.firstname || "",
        lastname: user.lastname || "",
        email: user.email || "",
        password: "",
        confirmPassword: "",
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
    setErrors((prevErrors) => ({ ...prevErrors, [name]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    const errors = {};
    if (values.firstname.trim() === "") {
      errors.firstname = "Le prénom est requis";
    }
    if (values.password.length < 12 || !hasLetter(values.password) || !hasNumber(values.password) || !hasSpecialChar(values.password)) {
      errors.password = "Le mot de passe ne respecte pas les critères de sécurité";
    }
    if (values.password === values.email) {
      errors.password = "Le mot de passe ne peut pas être identique à l'adresse email";
    }
    if (values.password !== values.confirmPassword) {
      errors.confirmPassword = "Les mots de passe ne sont pas identiques";
    }

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/user/signup", { firstname: values.firstname, lastname: values.lastname, password: values.password, token });
      if (!res.ok) {
        if (res.code === "INVALID_PASSWORD") setErrors({ password: "Le mot de passe ne respecte pas les critères de sécurité" });
        else throw res;
      }
      toast.success("Compte créé avec succès");
      navigate("/login");
    } catch (error) {
      captureError(error);
    }
    setLoading(false);
  };

  if (!user) return <p>Chargement...</p>;

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
      <h1 className="font-light">Complétez votre compte</h1>
      <h2 className="leading-tight">Renseignez vos informations</h2>
      <div className="flex flex-col">
        <label className="mb-2" htmlFor="firstname">
          Prénom
        </label>
        <input
          id="firstname"
          className={`input mb-2 ${submitted && errors.firstname ? "border-b-error" : "border-b-black"}`}
          name="firstname"
          value={values.firstname}
          onChange={handleChange}
          aria-invalid={submitted && errors.firstname ? true : undefined}
          aria-describedby={submitted && errors.firstname ? "firstname-error" : undefined}
        />
        {submitted && errors.firstname && (
          <p id="firstname-error" className="text-error flex items-center text-sm" aria-live="polite">
            <RiErrorWarningFill className="mr-2" aria-hidden="true" />
            {errors.firstname}
          </p>
        )}
      </div>
      <div className="flex flex-col">
        <label className="mb-2" htmlFor="lastname">
          Nom de famille
        </label>
        <input
          id="lastname"
          className={`input mb-2 ${submitted && errors.lastname ? "border-b-error" : "border-b-black"}`}
          name="lastname"
          value={values.lastname}
          onChange={handleChange}
          aria-invalid={submitted && errors.lastname ? true : undefined}
          aria-describedby={submitted && errors.lastname ? "lastname-error" : undefined}
        />
        {submitted && errors.lastname && (
          <p id="lastname-error" className="text-error flex items-center text-sm" aria-live="polite">
            <RiErrorWarningFill className="mr-2" aria-hidden="true" />
            {errors.lastname}
          </p>
        )}
      </div>
      <div className="flex flex-col">
        <div className="flex justify-between">
          <label className="mb-1" htmlFor="password">
            Mot de passe
          </label>
          <button type="button" className="flex cursor-pointer items-center gap-1" onClick={() => setShow(!show)}>
            {show ? <IoMdEyeOff className="text-blue-france" aria-hidden="true" /> : <IoMdEye className="text-blue-france" aria-hidden="true" />}
            <span className="text-blue-france text-xs font-bold">{show ? "CACHER" : "AFFICHER"}</span>
          </button>
        </div>
        <input
          id="password"
          className={`input mb-2 ${submitted && errors.password ? "border-b-error border-2" : "border-b-black"}`}
          name="password"
          type={show ? "text" : "password"}
          value={values.password}
          onChange={handleChange}
          aria-invalid={submitted && errors.password ? true : undefined}
          aria-describedby={submitted && errors.password ? "password-error password-criteria" : "password-criteria"}
        />
        {submitted && errors.password && (
          <p id="password-error" className="text-error flex items-center text-sm" aria-live="polite">
            <RiErrorWarningFill className="mr-2" aria-hidden="true" />
            {errors.password}
          </p>
        )}
      </div>
      <div id="password-criteria" className="mt-1 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          {values.password.length >= 12 ? (
            <AiFillCloseCircle className="text-success" aria-hidden="true" />
          ) : (
            <AiFillCloseCircle className="text-text-mention" aria-hidden="true" />
          )}
          <span className={`align-middle text-sm ${values.password.length >= 12 ? "text-success" : "text-text-mention"}`}>Au moins 12 caractères</span>
        </div>
        <div className="flex items-center gap-2">
          {hasLetter(values.password) ? <AiFillCloseCircle className="text-success" aria-hidden="true" /> : <AiFillCloseCircle className="text-text-mention" aria-hidden="true" />}
          <span className={`align-middle text-sm ${hasLetter(values.password) ? "text-success" : "text-text-mention"}`}>Au moins une lettre</span>
        </div>
        <div className="flex items-center gap-2">
          {hasNumber(values.password) ? <AiFillCloseCircle className="text-success" aria-hidden="true" /> : <AiFillCloseCircle className="text-text-mention" aria-hidden="true" />}
          <span className={`align-middle text-sm ${hasNumber(values.password) ? "text-success" : "text-text-mention"}`}>Au moins un chiffre</span>
        </div>

        <div className="flex items-center gap-2">
          {hasSpecialChar(values.password) ? (
            <AiFillCloseCircle className="text-success" aria-hidden="true" />
          ) : (
            <AiFillCloseCircle className="text-text-mention" aria-hidden="true" />
          )}
          <span className={`align-middle text-sm ${hasSpecialChar(values.password) ? "text-success" : "text-text-mention"}`}>Au moins un caractère spécial</span>
        </div>
      </div>
      <div className="mt-4 flex flex-col">
        <div className="flex justify-between">
          <label className="mb-1" htmlFor="confirm-password">
            Confirmation du mot de passe
          </label>
          <button type="button" className="flex cursor-pointer items-center gap-1" onClick={() => setShowConfirm(!showConfirm)}>
            {showConfirm ? <IoMdEyeOff className="text-blue-france" aria-hidden="true" /> : <IoMdEye className="text-blue-france" aria-hidden="true" />}
            <span className="text-blue-france text-xs font-bold">{showConfirm ? "CACHER" : "AFFICHER"}</span>
          </button>
        </div>
        <input
          id="confirm-password"
          className={`input mb-2 ${submitted && errors.confirmPassword ? "border-b-error border-2" : "border-b-black"}`}
          name="confirmPassword"
          type={showConfirm ? "text" : "password"}
          value={values.confirmPassword}
          onChange={handleChange}
          aria-invalid={submitted && errors.confirmPassword ? true : undefined}
          aria-describedby={submitted && errors.confirmPassword ? "confirm-password-error" : undefined}
        />
        {submitted && errors.confirmPassword && (
          <p id="confirm-password-error" className="text-error flex items-center text-sm" aria-live="polite">
            <RiErrorWarningFill className="mr-2" aria-hidden="true" />
            {errors.confirmPassword}
          </p>
        )}
      </div>
      <button type="submit" className="primary-btn mt-6 w-full" disabled={loading}>
        {loading ? "Chargement..." : "S'inscrire"}
      </button>
    </form>
  );
};

export default Signup;
